import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useFetch } from "../../hooks/useFetch";
import { convertShiftForDate, detectRealtimeShift, screenShiftsToDateShifts } from "../../lib/calculations";
import { useScreenStore } from "../../stores/ScreenStore";
import { Event, EventsResponseItem, GrouppedData, ScreenType } from "../../types/types";
import Spinner from "../../ui/Spinner/Spinner";
import Monitor from "./Monitor/Monitor";
import MonitorEdit from "./Monitor/MonitorEdit";

// 0: "equipment_item_id"
// 1: "event_date"
// 2: "shift_date"
// 3: "start"
// 4: "start_ts"
// 5: "finish"
// 6: "finish_ts"
// 7: "duration"
// 8: "state"
// 9: "equipment_item_status"
// 10: "personnel_id"
// 11: "access_token"
// 12: "equipment_item_division_id"
// 13: "personnel_division_id"
// 14: "state_changed"
// 15: "status_changed"
// 16: "person_changed"
// 17: "hour"
// 18: "is_break"

const MonitorContext = () => {
  const {id} = useParams()
  const [ loading, setLoading] = useState<boolean>(true)
  const { isEditMode, resetStore, setMonitor, setShifts, setUpdateDateTime, setRealTimeDateShift, setDateShift, equipments, dateShift, monitor, setMonitorData, dataIsLoaded, setDataIsLoaded, updateDateTime, monitorData, startUpdate, setStartUpdate } = useScreenStore();
  const [ monitorParams, setMonitorParams ] = useState(new URLSearchParams)
  const [ monitorUpdateParams, setMonitorUpdateParams ] = useState(new URLSearchParams)
  const [{ response, isLoading, error }, doFetchCurrentItem] = useFetch(`/directories/screens/${id}`);
  const [{ response: monitorResponse, isLoading: monitorLoading, error: monitorError }, fetchMonitorData] =
  useFetch(`/directories/ng_equipment_item_events/events?${monitorParams.toString()}`);
  const [{ response: monitorUpdateResponse, isLoading: monitorUpdateLoading, error: monitorUpdateError }, fetchMonitorUpdateData] =
  useFetch(`/directories/ng_equipment_item_events/events?${monitorUpdateParams.toString()}`);

  useEffect(() => {
    resetStore()
  }, [])

  useEffect(() => {
    if(!id || monitor) { 
      setLoading(false)
      return 
    }
    resetStore()
    setLoading(true)
    doFetchCurrentItem()
  }, [id, monitor])


  useEffect(() => {
    if(!response || isLoading) { return }
    const responseData = response.data as unknown as ScreenType;
    setMonitor(responseData);

    let convertedShifts = screenShiftsToDateShifts(responseData.attributes.working_shift_relations)
    let rTShift = detectRealtimeShift(convertedShifts, DateTime.local())
    let currentShift = convertShiftForDate(rTShift, null)

    setShifts(convertedShifts)
    setRealTimeDateShift(currentShift)
    setDateShift(currentShift)
    setLoading(false)
  },[response])

  useEffect(() => {
    if(!dateShift && !equipments) { return }
    if(equipments?.length == 0) {
      setMonitorData([])
      setDataIsLoaded(true)
      return
    }
    setDataIsLoaded(false)
    setParamsOnLoad()
    fetchMonitorData()
  },[dateShift])

  useEffect(() => {
    if(!error) { return }
    setLoading(false)
  }, [error])

  useEffect(() => {
    if(monitorLoading) { return }
    if(!monitor) { return }
    if(!monitorResponse) { return }
    let response = monitorResponse.data as unknown as EventsResponseItem[]
    let monitorData: GrouppedData = {}
    response?.map(item => {
      // FIX когда переходная смена чтобы брал нужные данные
      if(item.at == dateShift?.start_day.toFormat("yyyy-MM-dd")) {
        monitorData[item.equipment_item_id] = item.data
      }
    })
    setMonitorData(monitorData)
    setDataIsLoaded(true)
  }, [monitorResponse])

  useEffect(() => {
    if(!startUpdate || !updateDateTime) { return }
    if(!equipments) { return }
    if(equipments?.length == 0) {
      setMonitorData([])
      setDataIsLoaded(true)
      return 
    }
    setParamsOnUpdate()
    fetchMonitorUpdateData()
  }, [startUpdate, updateDateTime])

  useEffect(() => {
    if(!monitorUpdateResponse || monitorUpdateLoading || monitorUpdateError || !monitorData || !startUpdate) { return }
    let response = monitorUpdateResponse.data as unknown as EventsResponseItem[]
    updateMonitorData(response)
    setDataIsLoaded(true)
    setStartUpdate(false)
  }, [monitorUpdateResponse])


  function setParamsOnLoad() {
    let newParams = new URLSearchParams({});

    equipments?.map(e => {
      if(!e) {return}
      newParams.append("equipment_item_ids[]", String(e?.id!))
    })
    
    newParams.append("from", dateShift!.start_day.toSQL()!);
    // Отправлем дату начала смены иначе пришлет две даты при переходной смене
    newParams.append("to",  dateShift!.end_day.toSQL()!);
    newParams.append("day_time_from",  dateShift!.start_time.toFormat("HH:mm:ss")!);
    newParams.append("day_time_to", dateShift!.end_time.toFormat("HH:mm:ss")!);

    setMonitorParams(newParams)
  }

  function setParamsOnUpdate() {
    let newParams = new URLSearchParams({});

    equipments?.map(e => {
      if(!e) {return}
      newParams.append("equipment_item_ids[]", String(e?.id!))
    })

    newParams.append("from", updateDateTime!.minus({minutes: 30}).startOf('minute').toSQL()!);
    newParams.append("to",  updateDateTime!.toSQL()!);
    newParams.append("day_time_from",  updateDateTime!.minus({minutes: 30}).startOf('minute').toFormat("HH:mm:ss")!);
    newParams.append("day_time_to", updateDateTime!.startOf('minute').toFormat("HH:mm:ss")!);

    setMonitorUpdateParams(newParams)
  }

  function updateMonitorData(data: EventsResponseItem[]) {
    let newMonitorData: GrouppedData = {}
    data.map(item => {
      let startEvent = item.data[0]
      let oldData = monitorData[Number(item.equipment_item_id)] as Event[]
      let tail = oldData?.filter(od => Number(od[4]) < Number(startEvent[4]))
      if(!tail || tail.length === 0) {
        let oldDataStartEvent = oldData[0]
        let newDataFiltered = item.data?.filter(nd => Number(oldDataStartEvent[4]) < Number(nd[6]))
        let fte = newDataFiltered![0]
        let newTailEvent = [fte[0], fte[1], fte[2], oldDataStartEvent[3], oldDataStartEvent[4], fte[5], fte[6], Number(fte[6]) - Number(oldDataStartEvent[4]), fte[8], fte[9], fte[10], fte[11], fte[12], fte[13], fte[14], fte[15]]
        newDataFiltered[0] = newTailEvent
        newMonitorData[item.equipment_item_id] = newDataFiltered
      } else {
        let lte = tail![tail?.length! - 1]
        let newTailEvent = [lte[0], lte[1], lte[2], lte[3], lte[4], startEvent[3], startEvent[4], Number(startEvent[4]) - Number(lte[4]), lte[8], lte[9], lte[10], lte[11], lte[12], lte[13], lte[14], lte[15]]
        tail[tail?.length! - 1] = newTailEvent
        newMonitorData[item.equipment_item_id] = [...tail, ...item.data]
      }

    })
    setMonitorData(newMonitorData)
  }


  return (
    <>
      {loading ? <Spinner /> : isEditMode ? <MonitorEdit /> : <Monitor />}
    </>
  )
}

export default MonitorContext;


